import React from 'react';
import { Helmet } from 'react-helmet-async';
import { absoluteSiteUrl, BRAND_LOGO_PATH, getSiteOrigin } from '../../lib/siteConfig';

const SITE_NAME = 'RentaBahay';

function toAbsoluteImage(img: string | undefined): string {
  const fallback = `${getSiteOrigin()}${BRAND_LOGO_PATH}`;
  if (!img) return fallback;
  if (img.startsWith('http://') || img.startsWith('https://')) return img;
  const base = getSiteOrigin();
  return img.startsWith('/') ? `${base}${img}` : `${base}/${img}`;
}

export interface PropertySchemaFields {
  address?: string;
  city?: string;
  region?: string;
  price?: number;
  currency?: string;
  bedrooms?: number;
  bathrooms?: number;
  image?: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'property';
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  /** Optional structured data for property pages (address, price, location) */
  propertySchema?: PropertySchemaFields;
  /** Publish date for article type */
  publishedTime?: string;
  /** Preload this URL as the LCP image (homepage hero, etc.) */
  preloadLcpImage?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  canonicalUrl,
  noIndex = false,
  noFollow = false,
  propertySchema,
  publishedTime,
  preloadLcpImage,
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const imageUrl = toAbsoluteImage(image || undefined);
  const absoluteUrl = url ? absoluteSiteUrl(url) : undefined;
  const pageUrl = canonicalUrl || absoluteUrl || getSiteOrigin();
  const resolvedCanonical = canonicalUrl || absoluteUrl;

  const robotsContent =
    noIndex || noFollow
      ? [noIndex && 'noindex', noFollow && 'nofollow', noIndex && !noFollow && 'follow']
          .filter(Boolean)
          .join(', ')
      : 'index, follow';

  return (
    <Helmet>
      {preloadLcpImage && (
        <link rel="preload" as="image" href={preloadLcpImage} fetchPriority="high" />
      )}
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots Meta Tags — single tag so combinations stay valid */}
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
      
      {/* Geographic (GEO) - Philippines */}
      <meta name="geo.region" content="PH" />
      <meta name="geo.placename" content="Philippines" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={`${SITE_NAME} Real Estate`} />
      <meta property="og:locale" content="en_PH" />
      <meta property="og:locale:alternate" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content={`${SITE_NAME} Real Estate`} />
      
      {/* Structured Data - Property (Real Estate) */}
      {type === 'property' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateListing",
            "name": title.replace(` | ${SITE_NAME}`, ''),
            "description": description,
            "url": pageUrl,
            "image": propertySchema?.image ? toAbsoluteImage(propertySchema.image) : imageUrl,
            ...(propertySchema?.address && {
              "address": {
                "@type": "PostalAddress",
                "streetAddress": propertySchema.address,
                "addressLocality": propertySchema.city,
                "addressRegion": propertySchema.region,
                "addressCountry": "PH"
              }
            }),
            ...(propertySchema?.price != null && {
              "offers": {
                "@type": "Offer",
                "price": propertySchema.price,
                "priceCurrency": propertySchema.currency || "PHP"
              }
            }),
            ...(propertySchema?.bedrooms != null && { "numberOfRooms": propertySchema.bedrooms }),
            "provider": {
              "@type": "RealEstateAgent",
              "name": `${SITE_NAME} Real Estate`
            }
          })}
        </script>
      )}
    </Helmet>
  );
};
